import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import type { Team, TeamMember, UserSummary } from '../types';
import '../styles/teams.css';

export function TeamDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [team, setTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [suggestions, setSuggestions] = useState<UserSummary[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(null);
  const [savingTeam, setSavingTeam] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [region, setRegion] = useState('');
  const isTeamActive = team?.status === 'active';

  const loadMembers = async () => {
    if (!id) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await api.teamMembers(id);
      setTeam(response.team);
      setMembers(response.members || []);
      setName(response.team?.name || '');
      setDescription(response.team?.description || '');
      setRegion(response.team?.region || '');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadMembers();
  }, [id]);

  const isTeamAdmin = useMemo(() => {
    if (!user) {
      return false;
    }
    if (user.role === 'admin') {
      return true;
    }
    return members.some((member) => member.userId === user.id && member.role === 'admin');
  }, [user, members]);

  const handleAdd = async () => {
    if (!id || !selectedUser) {
      return;
    }
    setAdding(true);
    setError(null);
    try {
      await api.addTeamMember(id, { userId: selectedUser.id, role });
      setSearchTerm('');
      setSelectedUser(null);
      setSuggestions([]);
      setRole('member');
      await loadMembers();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setAdding(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const fetchSuggestions = async () => {
      if (searchTerm.trim().length < 2) {
        setSuggestions([]);
        return;
      }
      setSuggestionsLoading(true);
      try {
        const results = await api.searchUsers(searchTerm.trim());
        setSuggestions(results);
        if (results.length === 1) {
          setSelectedUser(results[0]);
        }
      } catch (err) {
        console.error('User search failed', err);
      } finally {
        setSuggestionsLoading(false);
      }
    };

    const handle = window.setTimeout(() => {
      void fetchSuggestions();
    }, 200);

    return () => {
      window.clearTimeout(handle);
      controller.abort();
    };
  }, [searchTerm]);

  const handleRemove = async (userId: string) => {
    if (!id) {
      return;
    }
    setRemovingId(userId);
    setError(null);
    try {
      await api.removeTeamMember(id, userId);
      await loadMembers();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRemovingId(null);
    }
  };

  if (!id) {
    return <div className="alert alert-error">Missing team id.</div>;
  }

  if (loading) {
    return <div className="loading">Loading team…</div>;
  }

  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }

  if (!team) {
    return <div className="alert alert-error">Team not found.</div>;
  }

  const adminCount = members.filter((m) => m.role === 'admin').length;
  const canEditTeam = isTeamAdmin;

  const handleSaveTeam = async () => {
    if (!id) return;
    setSavingTeam(true);
    setError(null);
    try {
      await api.updateTeam(id, { name, description, region });
      await loadMembers();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingTeam(false);
    }
  };

  return (
    <div className="teams-page">
      <div className="team-detail__header">
        <div>
          <h1>{team.name}</h1>
          <p className="hint">
            Status: <span className={`status status-${team.status === 'active' ? 'approved' : 'pending'}`}>
              {team.status || 'pending'}
            </span>
          </p>
        </div>
        <button className="button button-secondary" onClick={() => navigate('/teams')}>
          Back to teams
        </button>
      </div>

      <div className="team-detail__info card card-subtle">
        {canEditTeam ? (
          <div className="team-edit-form">
            <div className="form-row">
              <label htmlFor="team-name">Name</label>
              <input
                id="team-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="form-row">
              <label htmlFor="team-description">Description</label>
              <textarea
                id="team-description"
                rows={3}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Who is this team for and when should it be used?"
              />
            </div>
            <div className="form-row">
              <label htmlFor="team-region">Region</label>
              <input
                id="team-region"
                value={region}
                onChange={(event) => setRegion(event.target.value)}
                placeholder="e.g. EU, US, Global"
              />
            </div>
            <div className="form-row">
              <button className="button" type="button" onClick={handleSaveTeam} disabled={savingTeam}>
                {savingTeam ? 'Saving…' : 'Save team'}
              </button>
            </div>
          </div>
        ) : (
          <>
            {team.description ? <p className="team-card__description">{team.description}</p> : null}
            <div>
              <strong>Region</strong>
              <div>{team.region || 'Not specified'}</div>
            </div>
          </>
        )}
        <div>
          <strong>Created</strong>
          <div>{team.createdAt ? new Date(team.createdAt).toLocaleString() : '—'}</div>
        </div>
      </div>

      <section className="team-members card card-subtle">
        <div className="teams-section__header">
          <h2>Members</h2>
          <p className="hint">
            Team admins can add or remove members.
            {!isTeamActive ? ' This team is pending approval; member changes will be available once active.' : ''}
          </p>
        </div>

        {isTeamAdmin ? (
          <div className="team-add-form">
            <div className="form-row">
              <label htmlFor="member-search">User</label>
              <input
                id="member-search"
                type="search"
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setSelectedUser(null);
                }}
              />
              {suggestionsLoading ? <p className="hint">Searching…</p> : null}
              {!suggestionsLoading && suggestions.length > 0 ? (
                <div className="team-suggestions">
                  {suggestions.map((userSuggestion) => (
                    <button
                      type="button"
                      key={userSuggestion.id}
                      className={`team-suggestion${
                        selectedUser?.id === userSuggestion.id ? ' team-suggestion--selected' : ''
                      }`}
                      onClick={() => setSelectedUser(userSuggestion)}
                    >
                      <span className="team-suggestion__name">{userSuggestion.name}</span>
                      <span className="team-suggestion__email">{userSuggestion.email}</span>
                      <span className="team-suggestion__role">{userSuggestion.role}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="form-row">
              <label htmlFor="member-role">Role</label>
              <select
                id="member-role"
                value={role}
                onChange={(event) => setRole(event.target.value as 'admin' | 'member')}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="form-row">
              <button
                className="button"
                type="button"
                onClick={handleAdd}
                disabled={adding || !selectedUser || !isTeamActive}
              >
                {adding
                  ? 'Adding…'
                  : !isTeamActive
                  ? 'Pending approval'
                  : selectedUser
                  ? 'Add member'
                  : 'Select a user'}
              </button>
            </div>
          </div>
        ) : (
          <p className="hint">You are not a team admin; membership changes are disabled.</p>
        )}

        <div className="team-members">
          {members.length === 0 ? (
            <p className="hint">No members yet.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.id}>
                    <td>{member.userName || member.userId}</td>
                    <td>{member.userEmail || '—'}</td>
                    <td>
                      <span
                        className={`team-members__role-pill${
                          member.role === 'admin' ? ' team-members__role-pill--admin' : ''
                        }`}
                      >
                        {member.role === 'admin' ? 'Admin' : 'Member'}
                      </span>
                    </td>
                    <td>
                      {isTeamAdmin && !(member.role === 'admin' && adminCount <= 1) ? (
                        <div className="team-members__actions">
                          <button
                            className="button-tertiary"
                            type="button"
                            disabled={removingId === member.userId}
                            onClick={() => handleRemove(member.userId)}
                          >
                            {removingId === member.userId ? 'Removing…' : 'Remove'}
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
